import { CommonModule } from '@angular/common';
import {
  booleanAttribute,
  Component,
  ContentChild,
  EventEmitter,
  inject,
  Input,
  Output,
  TemplateRef
} from '@angular/core';
import { MatButtonModule } from '@angular/material/button';
import { MatDialogModule, MatDialogRef } from '@angular/material/dialog';
import { MatDividerModule } from '@angular/material/divider';
import { MatIconModule } from '@angular/material/icon';
import { MatProgressSpinnerModule } from '@angular/material/progress-spinner';

@Component({
  selector: 'mdc-dialog',
  imports: [CommonModule, MatDialogModule, MatIconModule, MatButtonModule, MatDividerModule, MatProgressSpinnerModule],
  host: { class: 'flex flex-col h-full' },

  template: `
    <div mat-dialog-title>
      <div class="flex items-center justify-between h-48">
        <h6 class="text-charcoal truncate">{{ dialogTitle }}</h6>
        @if (!hideClose) {
          <button matIconButton (click)="cancelAction()">
            <mat-icon>close</mat-icon>
          </button>
        }
      </div>
    </div>

    <mat-divider class="mx-20!" />

    <mat-dialog-content [class]="customClass">
      <ng-container [ngTemplateOutlet]="contentTemplate" />
    </mat-dialog-content>

    <mat-divider class="mx-20!" />

    <mat-dialog-actions class="justify-between mb-0">
      <ng-container [ngTemplateOutlet]="actionsTemplate ?? defaultActionsTemplate" />
    </mat-dialog-actions>

    <ng-template #defaultActionsTemplate>
      <button matButton="elevated" class="charcoal-button" [disabled]="disableCancel" (click)="cancelAction()">
        {{ cancelButtonText }}
      </button>
      <button matButton="elevated" class="red-button" [disabled]="disableSave" (click)="saveAction()">
        <div class="flex items-center gap-10 mr-[16px]">
          @if (isSaving) {
            <mat-spinner diameter="16" class="white-spinner" />
          }
          <div>{{ saveButtonText }}</div>
        </div>
      </button>
    </ng-template>
  `,
  styles: [
    `
      .white-spinner {
        --mat-progress-spinner-active-indicator-color: white;
      }
    `
  ]
})
export class MdcDialogComponent {
  @Input() dialogTitle = '';
  @Input() isSaving = false;
  @Input() saveButtonText = 'SAVE';
  @Input() cancelButtonText = 'CANCEL';
  @Input() closeOnSave = true;
  @Input() closeOnCancel = true;
  @Input() saveResult: any = true;
  @Input() cancelResult: any = false;
  @Input() disableSave: boolean;
  @Input() disableCancel: boolean;
  @Input() customClass = '';
  @Input({ transform: booleanAttribute }) hideClose = false;
  @Output() save = new EventEmitter<void>();
  @Output() cancel = new EventEmitter<void>();
  @ContentChild('contentTemplate') contentTemplate: TemplateRef<any>;
  @ContentChild('actionsTemplate') actionsTemplate: TemplateRef<any>;

  dialogRef = inject(MatDialogRef<MdcDialogComponent>);

  saveAction(): void {
    setTimeout(() => {
      this.save.emit();
      this.closeOnSave && this.dialogRef.close(this.saveResult);
    });
  }

  cancelAction(): void {
    setTimeout(() => {
      this.cancel.emit();
      this.closeOnCancel && this.dialogRef.close(this.cancelResult);
    });
  }
}
